import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { 
    ArrowLeft, 
    MapPin, 
    Ruler, 
    Bed, 
    Bathtub, 
    Tag,
    Calendar,
    Check,
    Pencil,
    FileText,
    Spinner,
    Lightning,
    Thermometer,
    CurrencyEur,
    Buildings,
    HouseLine,
    TreeStructure,
    Code,
    Copy,
    CheckCircle
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { ImageGallery } from '../components/ImageGallery';
import { PropertyMap } from '../components/PropertyMap';
import { PropertyEditDialog } from '../components/PropertyEditDialog';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
    { value: 'active', label: 'Aktiv' },
    { value: 'inactive', label: 'Inaktiv' },
    { value: 'sold', label: 'Verkauft' },
    { value: 'rented', label: 'Vermietet' },
];

const PROPERTY_TYPE_LABELS = {
    apartment: 'Wohnung',
    house: 'Haus',
    land: 'Grundstück',
    office: 'Büro',
    retail: 'Einzelhandel',
    warehouse: 'Lager/Halle',
    other: 'Sonstige'
};

const CONDITION_LABELS = {
    'ERSTBEZUG': 'Erstbezug',
    'NEUWERTIG': 'Neuwertig',
    'MODERNISIERT': 'Modernisiert',
    'VOLLSTAENDIG_RENOVIERT': 'Vollständig renoviert',
    'TEIL_VOLLRENOVIERUNGSBED': 'Renovierungsbedürftig',
    'SANIERT': 'Saniert',
    'GEPFLEGT': 'Gepflegt',
    'ABBRUCHREIF': 'Abbruchreif'
};

const ENERGY_CERT_LABELS = {
    'VERBRAUCH': 'Verbrauchsausweis',
    'BEDARF': 'Bedarfsausweis'
};

const HEATING_LABELS = {
    'zentralheizung': 'Zentralheizung',
    'etagenheizung': 'Etagenheizung',
    'fussbodenheizung': 'Fußbodenheizung',
    'fernwaerme': 'Fernwärme',
    'gas': 'Gas',
    'oel': 'Öl',
    'waermepumpe': 'Wärmepumpe',
    'solar': 'Solar',
    'pellet': 'Pellet'
};

export default function PropertyDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [property, setProperty] = useState(null);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [exportingPdf, setExportingPdf] = useState(false);
    const [embedDialogOpen, setEmbedDialogOpen] = useState(false);
    const [embedCopied, setEmbedCopied] = useState(false);

    const fetchProperty = async () => {
        try {
            const { data } = await axios.get(`${API}/api/properties/${id}`, { withCredentials: true });
            setProperty(data);
        } catch (error) {
            console.error('Error fetching property:', error);
            toast.error('Immobilie nicht gefunden');
            navigate('/properties');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProperty();
    }, [id, navigate]);

    const handleStatusChange = async (newStatus) => {
        setUpdating(true);
        try {
            const { data } = await axios.put(
                `${API}/api/properties/${id}`,
                { status: newStatus },
                { withCredentials: true }
            );
            setProperty(data);
            toast.success('Status aktualisiert');
        } catch (error) {
            toast.error('Fehler beim Aktualisieren');
        }
        setUpdating(false);
    };

    const [pdfProgress, setPdfProgress] = useState(null);

    const handleExportPdf = async () => {
        setExportingPdf(true);
        setPdfProgress({ percent: 0, message: 'PDF wird vorbereitet...' });
        try {
            // Start background generation
            await axios.post(`${API}/api/export/pdf/${id}/start`, {}, { withCredentials: true });

            // Poll progress
            const poll = setInterval(async () => {
                try {
                    const { data } = await axios.get(`${API}/api/export/pdf/${id}/progress`, { withCredentials: true });
                    setPdfProgress(data);

                    if (data.status === 'completed') {
                        clearInterval(poll);
                        setPdfProgress({ ...data, message: 'PDF wird heruntergeladen...' });
                        // Download
                        const response = await axios.get(`${API}/api/export/pdf/${id}/download`, {
                            withCredentials: true,
                            responseType: 'blob'
                        });
                        const url = window.URL.createObjectURL(new Blob([response.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', data.filename || `Expose_${id}.pdf`);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                        toast.success('PDF Export erfolgreich');
                        setExportingPdf(false);
                        setPdfProgress(null);
                    } else if (data.status === 'error') {
                        clearInterval(poll);
                        toast.error(data.message || 'PDF-Erstellung fehlgeschlagen');
                        setExportingPdf(false);
                        setPdfProgress(null);
                    }
                } catch (err) {
                    clearInterval(poll);
                    setExportingPdf(false);
                    setPdfProgress(null);
                }
            }, 1500);
        } catch (error) {
            toast.error('Fehler beim PDF Export');
            setExportingPdf(false);
            setPdfProgress(null);
        }
    };

    const formatPrice = (price, currency = 'EUR') => {
        return new Intl.NumberFormat('de-DE', {
            style: 'currency',
            currency: currency,
            maximumFractionDigits: 0
        }).format(price);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="space-y-6" data-testid="property-detail-loading">
                <Skeleton className="h-10 w-64" />
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2 space-y-6">
                        <Skeleton className="h-64" />
                        <Skeleton className="h-48" />
                    </div>
                    <Skeleton className="h-96" />
                </div>
            </div>
        );
    }

    if (!property) return null;

    const address = property.address || {};
    const fullAddress = [
        address.street,
        address.house_number,
        address.zip,
        address.city
    ].filter(Boolean).join(', ') || 'Keine Adresse';

    return (
        <div className="space-y-6" data-testid="property-detail-page">
            {/* Header */}
            <div className="flex items-center gap-4">
                <Link to="/properties">
                    <Button variant="ghost" size="icon" className="rounded-sm" data-testid="back-btn">
                        <ArrowLeft size={20} />
                    </Button>
                </Link>
                <div className="flex-1">
                    <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'var(--font-cabinet)' }}>
                        {property.title}
                    </h1>
                    <p className="text-muted-foreground flex items-center gap-1 mt-1">
                        <MapPin size={14} />
                        {fullAddress}
                    </p>
                </div>
                <Button 
                    variant="outline" 
                    className="rounded-sm"
                    onClick={() => setEditDialogOpen(true)}
                    data-testid="edit-property-btn"
                >
                    <Pencil size={16} className="mr-2" />
                    Bearbeiten
                </Button>
                <Button 
                    variant="outline" 
                    className="rounded-sm relative overflow-hidden"
                    onClick={handleExportPdf}
                    disabled={exportingPdf}
                    data-testid="export-pdf-btn"
                >
                    {exportingPdf && pdfProgress ? (
                        <>
                            <Spinner className="animate-spin mr-2" size={16} />
                            <span className="text-xs">{pdfProgress.percent || 0}%</span>
                            {pdfProgress.percent > 0 && (
                                <span className="absolute bottom-0 left-0 h-0.5 bg-primary transition-all" style={{ width: `${pdfProgress.percent}%` }} />
                            )}
                        </>
                    ) : (
                        <>
                            <FileText size={16} className="mr-2" />
                            PDF
                        </>
                    )}
                </Button>
                <Button 
                    variant="outline" 
                    className="rounded-sm"
                    onClick={() => setEmbedDialogOpen(true)}
                    data-testid="embed-single-btn"
                >
                    <Code size={16} className="mr-2" />
                    Embed
                </Button>
                <Select 
                    value={property.status} 
                    onValueChange={handleStatusChange}
                    disabled={updating}
                >
                    <SelectTrigger className="w-40 rounded-sm" data-testid="status-select">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {STATUS_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Image Gallery with Carousel */}
                    <Card className="border overflow-hidden" data-testid="images-card">
                        <CardContent className="p-4">
                            <ImageGallery 
                                images={property.images || []} 
                                propertyTitle={property.title}
                            />
                        </CardContent>
                    </Card>

                    {/* Description */}
                    <Card className="border" data-testid="description-card">
                        <CardHeader>
                            <CardTitle style={{ fontFamily: 'var(--font-cabinet)' }}>Beschreibung</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="whitespace-pre-wrap text-muted-foreground">
                                {property.description || 'Keine Beschreibung vorhanden'}
                            </p>
                        </CardContent>
                    </Card>

                    {/* Location Description */}
                    {property.location_description && (
                        <Card className="border" data-testid="location-desc-card">
                            <CardHeader>
                                <CardTitle style={{ fontFamily: 'var(--font-cabinet)' }}>Lage</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <p className="whitespace-pre-wrap text-muted-foreground">
                                    {property.location_description}
                                </p>
                            </CardContent>
                        </Card>
                    )}

                    {/* Flächen */}
                    {(property.area || property.plot_area || property.usable_area || property.total_area || property.rentable_area || property.balcony_terrace_area || property.basement_area || property.garden_area) && (
                        <Card className="border" data-testid="areas-card">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'var(--font-cabinet)' }}>
                                    <Ruler size={20} weight="fill" />
                                    Flächen
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-2 gap-4">
                                    {property.area && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Wohnfläche</p>
                                            <p className="font-semibold">{property.area} m²</p>
                                        </div>
                                    )}
                                    {property.plot_area && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Grundstücksfläche</p>
                                            <p className="font-semibold">{property.plot_area} m²</p>
                                        </div>
                                    )}
                                    {property.usable_area && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Nutzfläche</p>
                                            <p className="font-semibold">{property.usable_area} m²</p>
                                        </div>
                                    )}
                                    {property.total_area && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Gesamtfläche</p>
                                            <p className="font-semibold">{property.total_area} m²</p>
                                        </div>
                                    )}
                                    {property.rentable_area && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Vermietbare Fläche</p>
                                            <p className="font-semibold">{property.rentable_area} m²</p>
                                        </div>
                                    )}
                                    {property.balcony_terrace_area && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Balkon/Terrasse</p>
                                            <p className="font-semibold">{property.balcony_terrace_area} m²</p>
                                        </div>
                                    )}
                                    {property.basement_area && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Kellerfläche</p>
                                            <p className="font-semibold">{property.basement_area} m²</p>
                                        </div>
                                    )}
                                    {property.garden_area && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Gartenfläche</p>
                                            <p className="font-semibold">{property.garden_area} m²</p>
                                        </div>
                                    )}
                                    {property.num_balconies && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Anzahl Balkone</p>
                                            <p className="font-semibold">{property.num_balconies}</p>
                                        </div>
                                    )}
                                    {property.num_parking_spaces && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Stellplätze</p>
                                            <p className="font-semibold">{property.num_parking_spaces}</p>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Zustand & Baujahr */}
                    {(property.year_built || property.condition || property.last_renovation || property.floor !== null) && (
                        <Card className="border" data-testid="condition-card">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'var(--font-cabinet)' }}>
                                    <HouseLine size={20} weight="fill" />
                                    Zustand & Baujahr
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-2 gap-4">
                                    {property.year_built && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Baujahr</p>
                                            <p className="font-semibold">{property.year_built}</p>
                                        </div>
                                    )}
                                    {property.condition && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Zustand</p>
                                            <p className="font-semibold">{CONDITION_LABELS[property.condition] || property.condition}</p>
                                        </div>
                                    )}
                                    {property.last_renovation && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Letzte Renovierung</p>
                                            <p className="font-semibold">{property.last_renovation}</p>
                                        </div>
                                    )}
                                    {property.floor !== null && property.floor !== undefined && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Etage</p>
                                            <p className="font-semibold">{property.floor}{property.floors_total ? ` von ${property.floors_total}` : ''}</p>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Energieausweis */}
                    {(property.energy_certificate_type || property.energy_efficiency_class || property.energy_value || property.heating_type) && (
                        <Card className="border" data-testid="energy-card">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'var(--font-cabinet)' }}>
                                    <Lightning size={20} weight="fill" />
                                    Energieausweis
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-2 gap-4">
                                    {property.energy_certificate_type && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Art</p>
                                            <p className="font-semibold">{ENERGY_CERT_LABELS[property.energy_certificate_type] || property.energy_certificate_type}</p>
                                        </div>
                                    )}
                                    {property.energy_efficiency_class && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Effizienzklasse</p>
                                            <Badge className={`rounded-sm ${
                                                ['A+', 'A', 'B'].includes(property.energy_efficiency_class) ? 'bg-green-500' :
                                                ['C', 'D'].includes(property.energy_efficiency_class) ? 'bg-yellow-500' :
                                                'bg-orange-500'
                                            }`}>
                                                {property.energy_efficiency_class}
                                            </Badge>
                                        </div>
                                    )}
                                    {property.energy_value && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Energiekennwert</p>
                                            <p className="font-semibold">{property.energy_value} kWh/(m²·a)</p>
                                        </div>
                                    )}
                                    {property.energy_certificate_valid_until && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Gültig bis</p>
                                            <p className="font-semibold">{property.energy_certificate_valid_until}</p>
                                        </div>
                                    )}
                                    {property.heating_type && (
                                        <div className="col-span-2">
                                            <p className="text-sm text-muted-foreground">Heizungsart</p>
                                            <p className="font-semibold">{HEATING_LABELS[property.heating_type] || property.heating_type}</p>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Preise & Kosten */}
                    {(property.commission || property.price_per_sqm || property.rental_income || property.rental_income_target || property.additional_costs || property.warm_rent || property.heating_costs || property.deposit || property.net_cold_rent) && (
                        <Card className="border" data-testid="prices-card">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'var(--font-cabinet)' }}>
                                    <CurrencyEur size={20} weight="fill" />
                                    Preise & Kosten
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-2 gap-4">
                                    {property.price_per_sqm && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Preis pro m²</p>
                                            <p className="font-semibold">{formatPrice(property.price_per_sqm)}</p>
                                        </div>
                                    )}
                                    {property.commission && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Provision</p>
                                            <p className="font-semibold">{property.commission}</p>
                                        </div>
                                    )}
                                    {property.rental_income && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Ist-Mieteinnahmen p.a.</p>
                                            <p className="font-semibold">{formatPrice(property.rental_income)}</p>
                                        </div>
                                    )}
                                    {property.rental_income_target && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Soll-Mieteinnahmen p.a.</p>
                                            <p className="font-semibold">{formatPrice(property.rental_income_target)}</p>
                                        </div>
                                    )}
                                    {property.warm_rent && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Warmmiete</p>
                                            <p className="font-semibold">{formatPrice(property.warm_rent)}</p>
                                        </div>
                                    )}
                                    {property.net_cold_rent && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Nettokaltmiete</p>
                                            <p className="font-semibold">{formatPrice(property.net_cold_rent)}</p>
                                        </div>
                                    )}
                                    {property.additional_costs && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Nebenkosten</p>
                                            <p className="font-semibold">{formatPrice(property.additional_costs)}</p>
                                        </div>
                                    )}
                                    {property.heating_costs && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Heizkosten</p>
                                            <p className="font-semibold">{formatPrice(property.heating_costs)}</p>
                                        </div>
                                    )}
                                    {(property.deposit || property.deposit_text) && (
                                        <div>
                                            <p className="text-sm text-muted-foreground">Kaution</p>
                                            <p className="font-semibold">{property.deposit ? formatPrice(property.deposit) : property.deposit_text}</p>
                                        </div>
                                    )}
                                    {property.commission_note && (
                                        <div className="col-span-2">
                                            <p className="text-sm text-muted-foreground">Provisionshinweis</p>
                                            <p className="font-semibold">{property.commission_note}</p>
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Features */}
                    {property.features && property.features.length > 0 && (
                        <Card className="border" data-testid="features-card">
                            <CardHeader>
                                <CardTitle style={{ fontFamily: 'var(--font-cabinet)' }}>Ausstattung</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                    {property.features.map((feature, idx) => (
                                        <div key={idx} className="flex items-center gap-2 text-sm">
                                            <Check size={14} className="text-green-600 flex-shrink-0" />
                                            <span>{feature}</span>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {/* Map */}
                    <PropertyMap 
                        address={property.address} 
                        propertyTitle={property.title}
                    />
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Price Card */}
                    <Card className="border" data-testid="price-card">
                        <CardContent className="pt-6">
                            <div className="text-center">
                                <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                                    {property.marketing_type === 'rent' ? 'Miete' : 'Kaufpreis'}
                                </p>
                                <p className="text-3xl font-bold" style={{ fontFamily: 'var(--font-cabinet)' }}>
                                    {formatPrice(property.price, property.currency)}
                                </p>
                                {property.marketing_type === 'rent' && (
                                    <p className="text-muted-foreground">/Monat</p>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Details Card */}
                    <Card className="border" data-testid="details-card">
                        <CardHeader>
                            <CardTitle style={{ fontFamily: 'var(--font-cabinet)' }}>Details</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-muted-foreground">
                                    <Tag size={16} />
                                    <span>Typ</span>
                                </div>
                                <Badge variant="outline" className="rounded-sm">
                                    {PROPERTY_TYPE_LABELS[property.property_type] || property.property_type}
                                </Badge>
                            </div>
                            
                            <Separator />
                            
                            {property.area && (
                                <>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 text-muted-foreground">
                                            <Ruler size={16} />
                                            <span>Wohnfläche</span>
                                        </div>
                                        <span className="font-medium">{property.area} m²</span>
                                    </div>
                                    <Separator />
                                </>
                            )}
                            
                            {property.rooms && (
                                <>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 text-muted-foreground">
                                            <Bed size={16} />
                                            <span>Zimmer</span>
                                        </div>
                                        <span className="font-medium">{property.rooms}</span>
                                    </div>
                                    <Separator />
                                </>
                            )}
                            
                            {property.bathrooms && (
                                <>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 text-muted-foreground">
                                            <Bathtub size={16} />
                                            <span>Badezimmer</span>
                                        </div>
                                        <span className="font-medium">{property.bathrooms}</span>
                                    </div>
                                    <Separator />
                                </>
                            )}

                            {property.available_from && (
                                <>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 text-muted-foreground">
                                            <Calendar size={16} />
                                            <span>Verfügbar ab</span>
                                        </div>
                                        <span className="font-medium">{property.available_from}</span>
                                    </div>
                                    <Separator />
                                </>
                            )}
                            
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-muted-foreground">
                                    <Calendar size={16} />
                                    <span>Erstellt</span>
                                </div>
                                <span className="font-medium">{formatDate(property.created_at)}</span>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Object Numbers Card */}
                    {(property.object_nr_external || property.object_nr_internal || property.openimmo_id || property.external_id) && (
                        <Card className="border" data-testid="object-numbers-card">
                            <CardHeader>
                                <CardTitle style={{ fontFamily: 'var(--font-cabinet)' }}>Objektnummern</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                {property.object_nr_external && (
                                    <div>
                                        <p className="text-xs text-muted-foreground">Objektnr. (extern)</p>
                                        <p className="font-mono text-sm font-medium">{property.object_nr_external}</p>
                                    </div>
                                )}
                                {property.object_nr_internal && (
                                    <div>
                                        <p className="text-xs text-muted-foreground">Objektnr. (intern)</p>
                                        <p className="font-mono text-sm font-medium">{property.object_nr_internal}</p>
                                    </div>
                                )}
                                {property.external_id && !property.object_nr_external && (
                                    <div>
                                        <p className="text-xs text-muted-foreground">Externe ID</p>
                                        <p className="font-mono text-sm font-medium">{property.external_id}</p>
                                    </div>
                                )}
                                {property.openimmo_id && (
                                    <div>
                                        <p className="text-xs text-muted-foreground">OpenImmo ID</p>
                                        <p className="font-mono text-sm font-medium break-all">{property.openimmo_id}</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    )}

                    {/* Address Card */}
                    <Card className="border" data-testid="address-card">
                        <CardHeader>
                            <CardTitle style={{ fontFamily: 'var(--font-cabinet)' }}>Adresse</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-2 text-sm">
                                {address.street && (
                                    <p>{address.street} {address.house_number}</p>
                                )}
                                {(address.zip || address.city) && (
                                    <p>{address.zip} {address.city}</p>
                                )}
                                {address.country && (
                                    <p className="text-muted-foreground">{address.country}</p>
                                )}
                                {!address.street && !address.city && (
                                    <p className="text-muted-foreground">Keine Adresse vorhanden</p>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Edit Dialog */}
            <PropertyEditDialog
                property={property}
                open={editDialogOpen}
                onOpenChange={setEditDialogOpen}
                onSaved={() => {
                    fetchProperty();
                    setEditDialogOpen(false);
                }}
            />

            {/* Embed Code Dialog */}
            <Dialog open={embedDialogOpen} onOpenChange={setEmbedDialogOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'var(--font-cabinet)' }}>
                            Einzelne Immobilie einbetten
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <p className="text-sm text-muted-foreground">
                            Kopieren Sie diesen Code und fügen Sie ihn auf Ihrer Webseite ein. Es wird nur die Kachel dieser Immobilie angezeigt. Ein Klick darauf öffnet die Detailseite.
                        </p>
                        <div className="relative">
                            <Textarea
                                readOnly
                                className="font-mono text-xs h-32 resize-none rounded-sm"
                                value={`<div style="width:100%;max-width:450px;">\n  <iframe id="immo-${id}" src="${API}/embed/property/${id}" style="width:100%;border:none;overflow:hidden;" scrolling="no"></iframe>\n</div>\n<script>\nwindow.addEventListener('message',function(e){\n  if(e.data&&e.data.type==='immo-embed-resize'){\n    document.getElementById('immo-${id}').style.height=e.data.height+'px';\n  }\n});\n</script>`}
                                data-testid="embed-single-code"
                            />
                            <Button
                                variant="outline"
                                size="sm"
                                className="absolute top-2 right-2 rounded-sm"
                                onClick={() => {
                                    navigator.clipboard.writeText(
                                        `<div style="width:100%;max-width:450px;">\n  <iframe id="immo-${id}" src="${API}/embed/property/${id}" style="width:100%;border:none;overflow:hidden;" scrolling="no"></iframe>\n</div>\n<script>\nwindow.addEventListener('message',function(e){\n  if(e.data&&e.data.type==='immo-embed-resize'){\n    document.getElementById('immo-${id}').style.height=e.data.height+'px';\n  }\n});\n</script>`
                                    );
                                    setEmbedCopied(true);
                                    toast.success('Code kopiert!');
                                    setTimeout(() => setEmbedCopied(false), 2000);
                                }}
                                data-testid="copy-embed-btn"
                            >
                                {embedCopied ? <CheckCircle size={14} /> : <Copy size={14} />}
                            </Button>
                        </div>
                        <div className="text-xs text-muted-foreground space-y-1">
                            <p><strong>Vorschau:</strong> <a href={`${API}/embed/property/${id}`} target="_blank" rel="noopener noreferrer" className="text-primary underline">Kachel öffnen</a></p>
                            <p><strong>Tipp:</strong> Die Kachel passt sich automatisch an die Breite des Containers an. Mit <code>max-width</code> können Sie die maximale Breite begrenzen.</p>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
